import { rolesQuery, useDeleteRoleMutation } from "@common/api/roles"
import { useUsers } from "@common/api/users"
import type { RolePublic } from "@common/client"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { useReAuthGuard } from "@common/hooks/use-reauth-guard"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { memberCount } from "@/routes/_layout/settings/_panel/workspace/roles/-lib/member-count"
import { READ_ONLY_ROLE_NAME } from "@/routes/_layout/settings/-lib/roles"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/roles/_list/$roleId/delete",
)({
  component: DeleteRoleDialog,
})

function DeleteRoleDialog(): React.ReactElement | null {
  const { roleId } = Route.useParams()
  const { data } = useSuspenseQuery(rolesQuery())
  const { totalCount, isLoading } = useUsers({ role_ids: [roleId] })
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const deleteMutation = useDeleteRoleMutation()
  const { guard, handleFreshnessError, reAuthModal } = useReAuthGuard()
  const role = data.data.find((item) => item.id === roleId)
  const readOnly = data.data.find((item) => item.name === READ_ONLY_ROLE_NAME)

  if (role?.kind !== "custom") {
    return null
  }

  const close = () =>
    navigate({
      to: "/settings/workspace/roles/$roleId",
      params: { roleId },
      search: true,
    })

  const send = () =>
    deleteMutation.mutate(
      { role_id: roleId },
      {
        onSuccess: () => {
          showToast("Archived", `${role.name} archived.`, "success")
          navigate({ to: "/settings/workspace/roles", search: true })
        },
        onError: (error) => {
          if (handleFreshnessError(error, send)) return
          handleError(error, showToast)
        },
      },
    )

  return (
    <>
      <ConfirmDialog
        title={`Archive ${role.name}?`}
        description={consequence(totalCount, readOnly)}
        action="Archive"
        tone="default"
        pendingAction="Archiving…"
        pending={deleteMutation.isPending}
        disabled={isLoading}
        onConfirm={() => guard(send)}
        onCancel={close}
      />
      {reAuthModal}
    </>
  )
}

function consequence(
  holders: number,
  readOnly: RolePublic | undefined,
): string {
  return holders === 0
    ? "No one has this role."
    : `${memberCount(holders)} with this role will move to ${readOnly?.label ?? READ_ONLY_ROLE_NAME}.`
}
